import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Op, PlanItemDTO } from '@clear/contracts';
import { validateGateInput, type Gate, type Priority } from '@clear/domain';
import { formatNumber } from '@clear/i18n';
import { useLookups } from '../data/bootstrap';
import { useI18n } from '../i18n';
import { uuid } from '../lib/storage';
import { now } from '../lib/time';
import { outbox, type QueuedOp } from '../offline/outbox';
import { useAuthed } from '../session';
import { gateLabelFor } from './item';
import { useToast } from './Toasts';

type OpBody = Op extends infer O ? (O extends Op ? Omit<O, 'opId' | 'at'> : never) : never;

/** Queue an op for the signed-in user; returns the full op. */
export function useEnqueue() {
  const { user } = useAuthed();
  return useCallback(
    (body: OpBody, prev?: QueuedOp['prev']): Op => {
      const op = { ...body, opId: uuid(), at: now() } as Op;
      outbox.enqueue({ op, userId: user.id, ...(prev ? { prev } : {}) });
      return op;
    },
    [user.id],
  );
}

/** Floor actions for one gate: every tap applies instantly and offers a 10-second Undo. */
export function useGateActions(gate: Gate) {
  const { t } = useI18n();
  const lk = useLookups();
  const toast = useToast();
  const enqueue = useEnqueue();

  const set = useCallback(
    (
      item: PlanItemDTO,
      status: 'ok' | 'partial' | 'blocked',
      okQty?: number,
      reasonId?: string | null,
    ): boolean => {
      const v = validateGateInput(item, gate, { status, okQty: okQty ?? null, reasonId: reasonId ?? null });
      if (!v.ok) {
        toast({ message: t(`errors.${v.error}`), tone: 'error' });
        return false;
      }
      const entryId = uuid();
      const prev = item.gates[gate] ?? null;
      enqueue({
        type: 'gate.set',
        itemId: item.id,
        entryId,
        gate,
        status,
        ...(status === 'partial' ? { okQty } : {}),
        ...(reasonId ? { reasonId } : {}),
      });
      const m = lk?.model(item.modelId);
      let what = gateLabelFor(t, gate, status);
      if (status === 'partial') what += ` ${formatNumber(v.okQty)}`;
      if (reasonId) what += ` · ${lk?.reasonLabel(reasonId) ?? ''}`;
      toast({
        message: t('floor.saved', { model: `${m?.name ?? ''} ${m?.finish ?? ''}`.trim(), what }),
        onUndo: () => enqueue({ type: 'gate.undo', entryIds: [entryId] }, { [entryId]: prev }),
      });
      return true;
    },
    [gate, t, lk, toast, enqueue],
  );

  const allOk = useCallback(
    (items: PlanItemDTO[]) => {
      const entries = items
        .filter((i) => validateGateInput(i, gate, { status: 'ok' }).ok)
        .map((i) => ({ itemId: i.id, entryId: uuid(), prev: i.gates[gate] ?? null }));
      if (!entries.length) return;
      enqueue({
        type: 'gate.bulkOk',
        gate,
        entries: entries.map(({ itemId, entryId }) => ({ itemId, entryId })),
      });
      const prev = Object.fromEntries(entries.map((e) => [e.entryId, e.prev]));
      toast({
        message: t('floor.markedOk', { n: entries.length, gate: t(`gates.${gate}`) }),
        onUndo: () => enqueue({ type: 'gate.undo', entryIds: entries.map((e) => e.entryId) }, prev),
      });
    },
    [gate, t, toast, enqueue],
  );

  return {
    ok: (item: PlanItemDTO, observationReasonId?: string) => set(item, 'ok', undefined, observationReasonId),
    partial: (item: PlanItemDTO, okQty: number, reasonId?: string | null) =>
      set(item, 'partial', okQty, reasonId),
    blocked: (item: PlanItemDTO, reasonId: string) => set(item, 'blocked', undefined, reasonId),
    allOk,
  };
}

export function usePlannerActions() {
  const { t } = useI18n();
  const lk = useLookups();
  const toast = useToast();
  const enqueue = useEnqueue();
  const name = (modelId: string) => {
    const m = lk?.model(modelId);
    return m ? `${m.name} ${m.finish}` : '';
  };
  return {
    create: (
      s: { lineId: string; date: string; shiftId: string },
      mode: 'copy' | 'empty',
      sourcePlanId?: string,
    ) =>
      enqueue({ type: 'plan.create', planId: uuid(), ...s, mode, ...(sourcePlanId ? { sourcePlanId } : {}) }),
    publish: (planId: string) => enqueue({ type: 'plan.publish', planId }),
    add: (planId: string, modelId: string, qty: number, priority: Priority, published: boolean) => {
      enqueue({ type: 'item.add', planId, itemId: uuid(), modelId, qty, priority });
      toast({
        message: published
          ? t('planner.added', {
              model: name(modelId),
              qty: formatNumber(qty),
              who: t('planner.departments'),
            })
          : t('planner.addedDraft', { model: name(modelId), qty: formatNumber(qty) }),
      });
    },
    setQty: (item: PlanItemDTO, qty: number) => {
      enqueue({ type: 'item.update', itemId: item.id, qty });
      toast({ message: t('planner.qtyChanged', { model: name(item.modelId), qty: formatNumber(qty) }) });
    },
    cyclePriority: (item: PlanItemDTO) => {
      const next: Priority =
        item.priority === 'high' ? 'normal' : item.priority === 'normal' ? 'low' : 'high';
      enqueue({ type: 'item.update', itemId: item.id, priority: next });
    },
    reorder: (planId: string, order: string[]) => enqueue({ type: 'item.reorder', planId, order }),
    cancel: (item: PlanItemDTO) => {
      enqueue({ type: 'item.cancel', itemId: item.id });
      toast({ message: t('planner.cancelled', { model: name(item.modelId) }) });
    },
  };
}

export function useStartActions() {
  const { t } = useI18n();
  const lk = useLookups();
  const toast = useToast();
  const enqueue = useEnqueue();
  return (item: PlanItemDTO, lineName: string) => {
    enqueue({ type: 'item.start', itemId: item.id });
    const m = lk?.model(item.modelId);
    toast({
      message: t('supervisor.startedToast', { model: m?.name ?? '', line: lineName }),
      onUndo: () => enqueue({ type: 'item.unstart', itemId: item.id }),
    });
  };
}

/** Global: tell the user when the server refused a queued tap, then resync. */
export function useRejectionToasts() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  useEffect(
    () =>
      outbox.onRejected((rs) => {
        const rejectedIds = new Set(rs.map((r) => r.op.opId));
        // An undo of a tap that itself was refused needs no second message.
        const shown = rs.filter(
          (r) =>
            r.op.type !== 'gate.undo' ||
            !rs.some((x) => x.op.type !== 'gate.undo' && rejectedIds.has(x.op.opId)),
        );
        const first = shown[0];
        if (first) {
          const code = first.result.code ?? 'invalid';
          toast({
            message: t('errors.rejected', { reason: t(`errors.${code}`) }),
            tone: 'error',
            durationMs: 6_000,
          });
        }
        void qc.invalidateQueries({ queryKey: ['plan'] });
      }),
    [t, toast, qc],
  );
  useEffect(
    () =>
      outbox.onApplied((ops) => {
        // Plan creation copies items server-side: fetch them.
        if (ops.some((o) => o.type === 'plan.create')) void qc.invalidateQueries({ queryKey: ['plan'] });
      }),
    [qc],
  );
}
